import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Send,
  Sparkles,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input, Select } from "@/components/ui/Inputs";
import { PriorityBadge, TypeBadge } from "@/components/ui/Badges";
import {
  matrixKey,
  useMatrixItems,
  useCreateMatrixItem,
  useUpdateMatrixItem,
  useDeleteMatrixItem,
  type ResponseMatrixItem,
} from "@/hooks/useProjects";
import { useMe, useOrgUsers } from "@/hooks/useMe";
import {
  api,
  type ConsolidationProposal,
  type MatrixItemStatus,
  type OrgUserSummary,
  type Priority,
  type RequirementType,
} from "@/lib/api";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import DeadlineBanner from "@/components/DeadlineBanner";
import NextStepFooter from "@/components/NextStepFooter";
import { useProject } from "@/hooks/useProjects";
import { Avatar, UserPickerMenu, userLabel } from "@/components/UserPicker";
import { MatrixItemSidePanel } from "@/components/MatrixItemSidePanel";

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const TYPES: RequirementType[] = [
  "strategy",
  "proof",
  "pricing",
  "operations",
  "compliance",
  "creative",
  "admin",
];
const PRIORITIES: Priority[] = ["high", "medium", "low"];

const DELIVERABLE_TYPES: RequirementType[] = ["strategy", "proof", "creative", "operations"];

type StatusFilter = "all" | "open" | "done" | "na";
type OwnerFilter = "anyone" | "me" | "unassigned";

/**
 * Type-aware status options. Wire enum stays the same; user-facing labels
 * adapt to the row's type.
 */
function statusOptionsForType(type: RequirementType): { value: MatrixItemStatus; label: string }[] {
  switch (type) {
    case "compliance":
      return [
        { value: "not_started", label: "Pending" },
        { value: "complete", label: "Acknowledged" },
      ];
    case "admin":
      return [
        { value: "not_started", label: "Pending" },
        { value: "in_progress", label: "Working on it" },
        { value: "complete", label: "Done" },
      ];
    case "pricing":
      return [
        { value: "not_started", label: "Pending" },
        { value: "in_progress", label: "Pricing" },
        { value: "draft_ready", label: "Priced" },
        { value: "in_review", label: "In Review" },
        { value: "complete", label: "Submitted" },
      ];
    default:
      return [
        { value: "not_started", label: "Pending" },
        { value: "in_progress", label: "Drafting" },
        { value: "draft_ready", label: "Draft Ready" },
        { value: "in_review", label: "In Review" },
        { value: "complete", label: "Final" },
      ];
  }
}

function statusPillClass(item: ResponseMatrixItem): string {
  if (item.notApplicable) return "bg-gray-500/10 text-gray-500 border border-gray-500/20";
  if (item.status === "complete")
    return "bg-green-500/15 text-green-400 border border-green-500/20";
  if (item.status === "in_review")
    return "bg-orange-500/15 text-orange-400 border border-orange-500/20";
  if (item.status === "draft_ready")
    return "bg-purple-500/15 text-purple-400 border border-purple-500/20";
  if (item.status === "in_progress")
    return "bg-blue-500/15 text-blue-400 border border-blue-500/20";
  return "bg-gray-500/15 text-gray-400 border border-gray-500/20";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ResponseMatrix() {
  const params = useParams<{ id: string }>();
  const projectId = params.id;
  const qc = useQueryClient();

  const { data, isLoading, error } = useMatrixItems(projectId);
  const project = useProject(projectId);
  const me = useMe();
  const { data: orgUsersData } = useOrgUsers();
  const orgUsers = orgUsersData?.users ?? [];
  const usersById = useMemo(() => {
    const m = new Map<string, OrgUserSummary>();
    for (const u of orgUsers) m.set(u.id, u);
    return m;
  }, [orgUsers]);
  const myId = me.data?.user.id ?? null;

  const create = useCreateMatrixItem(projectId);
  const update = useUpdateMatrixItem(projectId);
  const remove = useDeleteMatrixItem(projectId);

  // Manual-add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [draftSection, setDraftSection] = useState("");
  const [draftRequirement, setDraftRequirement] = useState("");
  const [draftType, setDraftType] = useState<RequirementType>("strategy");
  const [draftPriority, setDraftPriority] = useState<Priority>("medium");

  const [pendingDelete, setPendingDelete] = useState<ResponseMatrixItem | null>(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [typeFilter, setTypeFilter] = useState<Set<RequirementType>>(new Set());
  const [priorityFilter, setPriorityFilter] = useState<Set<Priority>>(new Set());
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>("anyone");
  /**
   * Per-section explicit user override of collapsed state. Smart defaults
   * (default-collapse when many sections, auto-expand under filters,
   * auto-collapse fully-done sections) only apply when no override is set.
   * Persisted to localStorage so the user's mental layout survives reloads.
   */
  type CollapseOverride = "expanded" | "collapsed";
  const [userCollapseOverrides, setUserCollapseOverrides] = useState<Map<string, CollapseOverride>>(
    () => {
      if (!projectId || typeof window === "undefined") return new Map();
      try {
        const raw = window.localStorage.getItem(`matrix-collapse-${projectId}`);
        if (!raw) return new Map();
        const parsed = JSON.parse(raw) as Record<string, CollapseOverride>;
        return new Map(Object.entries(parsed));
      } catch {
        return new Map();
      }
    },
  );

  // Persist on change.
  useEffect(() => {
    if (!projectId || typeof window === "undefined") return;
    try {
      const obj: Record<string, CollapseOverride> = {};
      for (const [k, v] of userCollapseOverrides.entries()) obj[k] = v;
      window.localStorage.setItem(`matrix-collapse-${projectId}`, JSON.stringify(obj));
    } catch {
      // localStorage might be full / disabled — silently degrade.
    }
  }, [userCollapseOverrides, projectId]);

  // Bulk select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Section consolidation state
  const [consolidationOpen, setConsolidationOpen] = useState(false);
  const [consolidationLoading, setConsolidationLoading] = useState(false);
  const [consolidationProposals, setConsolidationProposals] = useState<
    ConsolidationProposal[] | null
  >(null);
  const [consolidationApplying, setConsolidationApplying] = useState(false);

  const openConsolidation = async () => {
    setConsolidationOpen(true);
    setConsolidationProposals(null);
    setConsolidationLoading(true);
    try {
      const result = await api.previewMatrixConsolidation(projectId);
      setConsolidationProposals(result.proposals);
    } catch (err) {
      toast.error((err as Error).message);
      setConsolidationOpen(false);
    } finally {
      setConsolidationLoading(false);
    }
  };

  const applyConsolidation = async (
    proposals: Pick<ConsolidationProposal, "target" | "sources">[],
  ) => {
    setConsolidationApplying(true);
    try {
      const result = await api.applyMatrixConsolidation(projectId, proposals);
      toast.success(
        `Reorganized ${result.totalUpdated} requirement${result.totalUpdated === 1 ? "" : "s"} across ${proposals.length} merge${proposals.length === 1 ? "" : "s"}.`,
      );
      qc.invalidateQueries({ queryKey: matrixKey(projectId) });
      setConsolidationOpen(false);
      setConsolidationProposals(null);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setConsolidationApplying(false);
    }
  };

  // Side panel state — id of the row currently open in the side panel.
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  // Hash sync: open the side panel from a deep link (#row=<uuid>) on mount,
  // and reflect open/close in the URL so users can copy a link.
  useEffect(() => {
    const readHash = () => {
      const m = window.location.hash.match(/row=([^&]+)/);
      if (m && m[1]) setOpenItemId(m[1]);
    };
    readHash();
    window.addEventListener("hashchange", readHash);
    return () => window.removeEventListener("hashchange", readHash);
  }, []);

  useEffect(() => {
    const expected = openItemId ? `#row=${openItemId}` : "";
    if (window.location.hash !== expected) {
      // Use replaceState so we don't pollute browser history with each open.
      const url = `${window.location.pathname}${window.location.search}${expected}`;
      window.history.replaceState(null, "", url);
    }
  }, [openItemId]);

  const canAdd = draftSection.trim() && draftRequirement.trim();

  const closeAddForm = () => {
    setShowAddForm(false);
    setDraftSection("");
    setDraftRequirement("");
    setDraftType("strategy");
    setDraftPriority("medium");
  };

  const handleAdd = async () => {
    if (!canAdd) return;
    await create.mutateAsync({
      section: draftSection,
      requirement: draftRequirement,
      type: draftType,
      priority: draftPriority,
    });
    setDraftSection("");
    setDraftRequirement("");
    toast.success("Requirement added.");
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success("Row removed.");
      setPendingDelete(null);
    } catch (err) {
      toast.error((err as Error).message ?? "Couldn't remove the row.");
    }
  };

  if (isLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Requirements Tracker" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Requirements Tracker" subtitle="Couldn't load tracker" />
        <div className="p-8">
          <Card>
            <div className="text-sm text-red-400 font-medium">{(error as Error).message}</div>
          </Card>
        </div>
      </Layout>
    );
  }

  const items: ResponseMatrixItem[] = data ?? [];

  // ---- Counts (full dataset, ignoring filters) ----
  const totalCount = items.length;
  const naCount = items.filter((i) => i.notApplicable).length;
  const openCount = items.filter((i) => !i.notApplicable && i.status !== "complete").length;
  const doneCount = items.filter((i) => !i.notApplicable && i.status === "complete").length;

  // ---- Filtering ----
  const q = search.trim().toLowerCase();
  const filtered = items.filter((i) => {
    if (statusFilter === "open" && (i.notApplicable || i.status === "complete")) return false;
    if (statusFilter === "done" && (i.notApplicable || i.status !== "complete")) return false;
    if (statusFilter === "na" && !i.notApplicable) return false;
    if (typeFilter.size > 0 && !typeFilter.has(i.type)) return false;
    if (priorityFilter.size > 0 && !priorityFilter.has(i.priority)) return false;
    if (ownerFilter === "me" && i.ownerUserId !== myId) return false;
    if (ownerFilter === "unassigned" && i.ownerUserId !== null) return false;
    if (q && !i.requirement.toLowerCase().includes(q) && !i.section.toLowerCase().includes(q))
      return false;
    return true;
  });

  // ---- Group by section ----
  const sectionMap = new Map<string, ResponseMatrixItem[]>();
  for (const item of filtered) {
    if (!sectionMap.has(item.section)) sectionMap.set(item.section, []);
    sectionMap.get(item.section)!.push(item);
  }
  // Section totals/done are computed from the FULL items list (not filtered),
  // so the progress count reflects reality, not what's currently visible.
  // Per-section type counts let us pick a "dominant work mode" hint:
  //   ack-dominant     → "47 to acknowledge" + Acknowledge-all button
  //   deliverable-     → "12 to draft"
  //     dominant
  //   mixed            → "X open"
  interface SectionStats {
    total: number;
    done: number;
    ackOpen: number;
    deliverableOpen: number;
    pricingOpen: number;
    totalOpen: number;
  }
  const sectionStats = new Map<string, SectionStats>();
  for (const item of items) {
    const s = sectionStats.get(item.section) ?? {
      total: 0,
      done: 0,
      ackOpen: 0,
      deliverableOpen: 0,
      pricingOpen: 0,
      totalOpen: 0,
    };
    if (!item.notApplicable) {
      s.total += 1;
      if (item.status === "complete") {
        s.done += 1;
      } else {
        s.totalOpen += 1;
        if (item.type === "compliance" || item.type === "admin") s.ackOpen += 1;
        else if (DELIVERABLE_TYPES.includes(item.type)) s.deliverableOpen += 1;
        else if (item.type === "pricing") s.pricingOpen += 1;
      }
    }
    sectionStats.set(item.section, s);
  }
  const groups: Array<{
    section: string;
    items: ResponseMatrixItem[];
    total: number;
    done: number;
  }> = [];
  for (const [section, sectionItems] of sectionMap) {
    const stats = sectionStats.get(section) ?? { total: 0, done: 0 };
    groups.push({
      section,
      items: sectionItems,
      total: stats.total,
      done: stats.done,
    });
  }

  const isOpenDeliverablesPreset =
    statusFilter === "open" &&
    typeFilter.size === DELIVERABLE_TYPES.length &&
    DELIVERABLE_TYPES.every((t) => typeFilter.has(t)) &&
    priorityFilter.size === 0 &&
    ownerFilter === "anyone";

  const applyOpenDeliverablesPreset = () => {
    setStatusFilter("open");
    setTypeFilter(new Set(DELIVERABLE_TYPES));
    setPriorityFilter(new Set());
    setOwnerFilter("anyone");
    setSearch("");
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setTypeFilter(new Set());
    setPriorityFilter(new Set());
    setOwnerFilter("anyone");
    setSearch("");
  };

  const filtersActive =
    statusFilter !== "all" ||
    typeFilter.size > 0 ||
    priorityFilter.size > 0 ||
    ownerFilter !== "anyone" ||
    search.length > 0;

  /**
   * "Narrowing" filter: the user has actively reduced the set, not just
   * landed on the default "open" view. Drives auto-expand of sections so
   * matches are visible without manual clicks.
   */
  const isNarrowingFilter =
    search.length > 0 ||
    statusFilter === "done" ||
    statusFilter === "na" ||
    typeFilter.size > 0 ||
    priorityFilter.size > 0 ||
    ownerFilter !== "anyone";

  /**
   * Smart-default collapse logic. The user's explicit override always wins.
   * Otherwise: expand under narrowing filters, collapse when section is 100%
   * done, collapse when many sections, expand otherwise.
   */
  const isSectionCollapsed = (sectionName: string): boolean => {
    const override = userCollapseOverrides.get(sectionName);
    if (override !== undefined) return override === "collapsed";
    if (isNarrowingFilter) return false;
    const stats = sectionStats.get(sectionName);
    if (stats && stats.total > 0 && stats.done === stats.total) return true;
    if (groups.length > 5) return true;
    return false;
  };

  const toggleType = (t: RequirementType) =>
    setTypeFilter((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });

  const togglePriority = (p: Priority) =>
    setPriorityFilter((prev) => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });

  // Toggle inverts whatever the row is currently rendering as. Writes an
  // explicit override so smart-defaults stop steering from then on.
  const toggleSection = (section: string) => {
    const currentlyCollapsed = isSectionCollapsed(section);
    setUserCollapseOverrides((prev) => {
      const next = new Map(prev);
      next.set(section, currentlyCollapsed ? "expanded" : "collapsed");
      return next;
    });
  };

  const collapseAll = () =>
    setUserCollapseOverrides((prev) => {
      const next = new Map(prev);
      for (const g of groups) next.set(g.section, "collapsed");
      return next;
    });

  const expandAll = () =>
    setUserCollapseOverrides((prev) => {
      const next = new Map(prev);
      for (const g of groups) next.set(g.section, "expanded");
      return next;
    });

  // ---- Bulk select handlers ----
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSectionSelect = (section: string) => {
    const sectionItemIds = sectionMap.get(section)?.map((i) => i.id) ?? [];
    if (sectionItemIds.length === 0) return;
    const allSelected = sectionItemIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of sectionItemIds) next.delete(id);
      } else {
        for (const id of sectionItemIds) next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  /**
   * Apply the same patch to a list of item IDs in parallel. Caps concurrency
   * at 8 to stay friendly to the rate limiter. A server-side bulk endpoint
   * is a reasonable next-pass optimization.
   */
  const runBulkPatch = async (
    ids: string[],
    patch: Partial<ResponseMatrixItem>,
    successMsg: (count: number) => string,
  ): Promise<{ done: number; failed: number }> => {
    if (ids.length === 0) return { done: 0, failed: 0 };
    setBulkBusy(true);
    let done = 0;
    let failed = 0;

    const CONCURRENCY = 8;
    const queue = [...ids];
    const workers: Promise<void>[] = [];
    const worker = async () => {
      while (queue.length > 0) {
        const id = queue.shift()!;
        try {
          await api.updateMatrixItem(id, patch);
          done++;
        } catch (err) {
          failed++;
        }
      }
    };
    for (let i = 0; i < CONCURRENCY; i++) workers.push(worker());
    await Promise.all(workers);

    await qc.invalidateQueries({ queryKey: matrixKey(projectId) });
    setBulkBusy(false);

    if (failed === 0) {
      toast.success(successMsg(done));
    } else {
      toast.warning(`${successMsg(done)} — ${failed} failed.`);
    }
    return { done, failed };
  };

  // Bulk action bar handlers — operate on `selectedIds`.
  const bulkAcknowledge = async () => {
    const ids = Array.from(selectedIds);
    const r = await runBulkPatch(ids, { status: "complete" }, (n) => `Marked done (${n})`);
    if (r.failed === 0) clearSelection();
  };
  const bulkMarkNa = async () => {
    const ids = Array.from(selectedIds);
    const r = await runBulkPatch(
      ids,
      { notApplicable: true },
      (n) => `Marked not applicable (${n})`,
    );
    if (r.failed === 0) clearSelection();
  };
  const bulkAssign = async (ownerUserId: string | null) => {
    const ids = Array.from(selectedIds);
    const label = ownerUserId
      ? `Assigned to ${userLabel(usersById.get(ownerUserId))}`
      : "Cleared assignee";
    const r = await runBulkPatch(ids, { ownerUserId }, (n) => `${label} (${n})`);
    if (r.failed === 0) clearSelection();
  };

  /**
   * Acknowledge every open compliance/admin row in a section. Used by the
   * "Acknowledge all" button on ack-dominant section headers — the single
   * biggest UX win for compliance-heavy RFPs.
   */
  const ackAllInSection = async (sectionName: string) => {
    const ids = items
      .filter(
        (i) =>
          i.section === sectionName &&
          (i.type === "compliance" || i.type === "admin") &&
          !i.notApplicable &&
          i.status !== "complete",
      )
      .map((i) => i.id);
    if (ids.length === 0) {
      toast.info("Nothing to acknowledge in this section.");
      return;
    }
    await runBulkPatch(ids, { status: "complete" }, (n) => `Acknowledged ${n} in "${sectionName}"`);
  };

  // Selected count, rendered in the action bar
  const selectedCount = selectedIds.size;

  return (
    <Layout projectId={projectId}>
      {project.data && <DeadlineBanner project={project.data} />}
      <PageHeader
        title="Requirements Tracker"
        subtitle={
          totalCount === 0
            ? "Nothing parsed yet"
            : `${doneCount} of ${totalCount - naCount} done` +
              (naCount > 0 ? ` · ${naCount} N/A` : "")
        }
        actions={<TeamworkSyncButton projectId={projectId} items={items} />}
      />

      <div className="p-8 pb-24 space-y-6">
        {/* Bulk action bar — appears when at least one row is selected */}
        {selectedCount > 0 && (
          <BulkActionBar
            count={selectedCount}
            busy={bulkBusy}
            orgUsers={orgUsers}
            myId={myId}
            onAcknowledge={bulkAcknowledge}
            onMarkNa={bulkMarkNa}
            onAssign={bulkAssign}
            onClear={clearSelection}
          />
        )}

        {/* Filter bar */}
        {totalCount > 0 && (
          <Card>
            <div className="space-y-3">
              {/* Top row: search + saved view + add */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Search requirements or sections…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 focus:bg-white/[0.07]"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                <Button
                  variant={isOpenDeliverablesPreset ? "primary" : "secondary"}
                  size="sm"
                  onClick={applyOpenDeliverablesPreset}
                  title="Filter to deliverables that aren't done — your writing list"
                >
                  Open deliverables only
                </Button>

                {filtersActive && !isOpenDeliverablesPreset && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} icon={<X size={12} />}>
                    Clear filters
                  </Button>
                )}

                <div className="flex-1" />

                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Sparkles size={14} />}
                  onClick={openConsolidation}
                  title="Ask AI to merge over-fragmented sections"
                >
                  Reorganize sections
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Plus size={14} />}
                  onClick={() => setShowAddForm((v) => !v)}
                >
                  Add manually
                </Button>
              </div>

              {/* Filter chips — one row per group with a label column. */}
              <div
                className="pt-3 border-t space-y-2"
                style={{ borderColor: "rgba(229,231,235,0.05)" }}
              >
                <FilterRow label="Status">
                  <FilterChip
                    active={statusFilter === "all"}
                    onClick={() => setStatusFilter("all")}
                  >
                    All ({totalCount})
                  </FilterChip>
                  <FilterChip
                    active={statusFilter === "open"}
                    onClick={() => setStatusFilter("open")}
                  >
                    Open ({openCount})
                  </FilterChip>
                  <FilterChip
                    active={statusFilter === "done"}
                    onClick={() => setStatusFilter("done")}
                  >
                    Done ({doneCount})
                  </FilterChip>
                  {naCount > 0 && (
                    <FilterChip
                      active={statusFilter === "na"}
                      onClick={() => setStatusFilter("na")}
                    >
                      N/A ({naCount})
                    </FilterChip>
                  )}
                </FilterRow>

                <FilterRow label="Type">
                  {TYPES.map((t) => (
                    <FilterChip key={t} active={typeFilter.has(t)} onClick={() => toggleType(t)}>
                      {t}
                    </FilterChip>
                  ))}
                </FilterRow>

                <FilterRow label="Priority">
                  {PRIORITIES.map((p) => (
                    <FilterChip
                      key={p}
                      active={priorityFilter.has(p)}
                      onClick={() => togglePriority(p)}
                    >
                      {p}
                    </FilterChip>
                  ))}
                </FilterRow>

                <FilterRow label="Owner">
                  <FilterChip
                    active={ownerFilter === "anyone"}
                    onClick={() => setOwnerFilter("anyone")}
                  >
                    Anyone
                  </FilterChip>
                  <FilterChip active={ownerFilter === "me"} onClick={() => setOwnerFilter("me")}>
                    Assigned to me
                  </FilterChip>
                  <FilterChip
                    active={ownerFilter === "unassigned"}
                    onClick={() => setOwnerFilter("unassigned")}
                  >
                    Unassigned
                  </FilterChip>
                </FilterRow>
              </div>
            </div>
          </Card>
        )}

        {/* Manual-add form (collapsible) */}
        {showAddForm && (
          <Card>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
                >
                  <Plus size={16} style={{ color: "#F97316" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white">Add a requirement manually</h3>
                  <p
                    className="text-xs mt-0.5 flex items-start gap-1.5"
                    style={{ color: "#9CA3AF" }}
                  >
                    <Info size={12} className="mt-0.5 flex-shrink-0" />
                    <span>
                      Use this if the AI parser missed something in the RFP, or to track an internal
                      must-do for this bid (e.g., "include MOL case study"). Parsed RFP items appear
                      automatically below.
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={closeAddForm}
                className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/5 flex-shrink-0"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3">
                <Input
                  label="Section"
                  placeholder="e.g., Strategy, Pricing"
                  value={draftSection}
                  onChange={(e) => setDraftSection(e.target.value)}
                />
              </div>
              <div className="md:col-span-4">
                <Input
                  label="Requirement"
                  placeholder="What does the RFP (or your team) want?"
                  value={draftRequirement}
                  onChange={(e) => setDraftRequirement(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Select
                  label="Type"
                  value={draftType}
                  onChange={(e) => setDraftType(e.target.value as RequirementType)}
                  options={TYPES.map((t) => ({ value: t, label: t }))}
                />
              </div>
              <div className="md:col-span-2">
                <Select
                  label="Priority"
                  value={draftPriority}
                  onChange={(e) => setDraftPriority(e.target.value as Priority)}
                  options={PRIORITIES.map((p) => ({ value: p, label: p }))}
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  variant="primary"
                  onClick={handleAdd}
                  disabled={!canAdd || create.isPending}
                  icon={<Plus size={14} />}
                  className="w-full"
                >
                  Add
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Item list */}
        {totalCount === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-white font-medium">No requirements yet.</p>
              <p className="text-xs mt-1" style={{ color: "#6B7280" }}>
                Upload the RFP on the New RFP page to extract requirements automatically, or use{" "}
                <button
                  onClick={() => setShowAddForm(true)}
                  className="underline hover:text-orange-400"
                >
                  Add manually
                </button>{" "}
                above.
              </p>
            </div>
          </Card>
        ) : filtered.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-white font-medium">No requirements match your filters.</p>
              <p className="text-xs mt-1" style={{ color: "#6B7280" }}>
                {filtersActive ? (
                  <button onClick={clearFilters} className="underline hover:text-orange-400">
                    Clear filters
                  </button>
                ) : (
                  "Adjust filters above."
                )}
              </p>
            </div>
          </Card>
        ) : (
          <>
            {/* Expand/collapse all */}
            {groups.length > 1 && (
              <div className="flex items-center justify-between text-xs px-1">
                <span style={{ color: "#9CA3AF" }}>
                  {filtered.length} requirement{filtered.length === 1 ? "" : "s"} in {groups.length}{" "}
                  section{groups.length === 1 ? "" : "s"}
                </span>
                <div className="flex items-center gap-3">
                  <button onClick={expandAll} className="text-gray-400 hover:text-white">
                    Expand all
                  </button>
                  <span className="text-gray-600">·</span>
                  <button onClick={collapseAll} className="text-gray-400 hover:text-white">
                    Collapse all
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {groups.map((group) => {
                const sectionItemIds = group.items.map((i) => i.id);
                const allSelectedInSection =
                  sectionItemIds.length > 0 && sectionItemIds.every((id) => selectedIds.has(id));
                const someSelectedInSection = sectionItemIds.some((id) => selectedIds.has(id));
                const stats = sectionStats.get(group.section);
                return (
                  <SectionGroup
                    key={group.section}
                    section={group.section}
                    items={group.items}
                    total={group.total}
                    done={group.done}
                    ackOpen={stats?.ackOpen ?? 0}
                    deliverableOpen={stats?.deliverableOpen ?? 0}
                    pricingOpen={stats?.pricingOpen ?? 0}
                    totalOpen={stats?.totalOpen ?? 0}
                    collapsed={isSectionCollapsed(group.section)}
                    onToggle={() => toggleSection(group.section)}
                    onSelectAll={() => toggleSectionSelect(group.section)}
                    allSelected={allSelectedInSection}
                    someSelected={someSelectedInSection && !allSelectedInSection}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    orgUsers={orgUsers}
                    usersById={usersById}
                    myId={myId}
                    onPatch={(itemId, patch) => update.mutate({ itemId, patch })}
                    onDelete={(item) => setPendingDelete(item)}
                    onOpen={(itemId) => setOpenItemId(itemId)}
                    onAcknowledgeAll={() => ackAllInSection(group.section)}
                    bulkBusy={bulkBusy}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Remove this row?"
        description={
          pendingDelete
            ? `"${pendingDelete.requirement}" will be removed from the tracker. If this is from the parsed RFP, consider "Mark N/A" instead — it keeps the audit trail.`
            : ""
        }
        confirmLabel="Remove"
        onConfirm={handleConfirmDelete}
        busy={remove.isPending}
      />

      {/* Section consolidation modal */}
      {consolidationOpen && (
        <ConsolidationModal
          loading={consolidationLoading}
          proposals={consolidationProposals}
          applying={consolidationApplying}
          onClose={() => {
            if (consolidationApplying) return;
            setConsolidationOpen(false);
            setConsolidationProposals(null);
          }}
          onApply={applyConsolidation}
        />
      )}

      {/* Side panel — opens when openItemId matches an existing item. */}
      {openItemId &&
        (() => {
          const openItem = items.find((i) => i.id === openItemId);
          if (!openItem) return null;
          return (
            <MatrixItemSidePanel
              item={openItem}
              orgUsers={orgUsers}
              usersById={usersById}
              myId={myId}
              onClose={() => setOpenItemId(null)}
              onPatch={(patch) => update.mutate({ itemId: openItem.id, patch })}
              onDelete={() => {
                setOpenItemId(null);
                setPendingDelete(openItem);
              }}
            />
          );
        })()}

      <NextStepFooter currentStep="matrix" projectId={projectId} />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Bulk action bar (sticky at top of page when selection > 0)
// ---------------------------------------------------------------------------

interface BulkActionBarProps {
  count: number;
  busy: boolean;
  orgUsers: OrgUserSummary[];
  myId: string | null;
  onAcknowledge: () => void;
  onMarkNa: () => void;
  onAssign: (userId: string | null) => void;
  onClear: () => void;
}

function BulkActionBar({
  count,
  busy,
  orgUsers,
  myId,
  onAcknowledge,
  onMarkNa,
  onAssign,
  onClear,
}: BulkActionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  return (
    <div
      className="rounded-xl border px-4 py-2.5 flex items-center gap-3 flex-wrap sticky top-0 z-10"
      style={{
        backgroundColor: "rgba(249,115,22,0.08)",
        borderColor: "rgba(249,115,22,0.25)",
        backdropFilter: "blur(8px)",
      }}
    >
      <span className="text-sm font-semibold text-orange-300">{count} selected</span>

      <div className="flex-1" />

      <Button
        variant="secondary"
        size="sm"
        loading={busy}
        icon={<CheckCircle2 size={14} />}
        onClick={onAcknowledge}
      >
        Mark done
      </Button>

      <Button
        variant="secondary"
        size="sm"
        loading={busy}
        icon={<Ban size={14} />}
        onClick={onMarkNa}
      >
        Mark N/A
      </Button>

      <div ref={pickerRef} className="relative">
        <Button
          variant="secondary"
          size="sm"
          loading={busy}
          icon={<UserPlus size={14} />}
          onClick={() => setPickerOpen((v) => !v)}
        >
          Assign
        </Button>
        {pickerOpen && (
          <UserPickerMenu
            users={orgUsers}
            myId={myId}
            onPick={(id) => {
              setPickerOpen(false);
              onAssign(id);
            }}
            allowUnassign
          />
        )}
      </div>

      <button
        onClick={onClear}
        className="text-xs text-gray-300 hover:text-white px-2 py-1 rounded-lg hover:bg-white/5"
      >
        Clear
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Consolidation modal — review AI proposals to merge over-fragmented sections.
// ---------------------------------------------------------------------------

interface ConsolidationModalProps {
  loading: boolean;
  proposals: ConsolidationProposal[] | null;
  applying: boolean;
  onClose: () => void;
  onApply: (proposals: Pick<ConsolidationProposal, "target" | "sources">[]) => Promise<void>;
}

function ConsolidationModal({
  loading,
  proposals,
  applying,
  onClose,
  onApply,
}: ConsolidationModalProps) {
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  // When proposals first arrive, default to all accepted.
  useEffect(() => {
    if (proposals) setAccepted(new Set(proposals.map((_, i) => i)));
  }, [proposals]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !applying) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [applying, onClose]);

  const toggleAccepted = (idx: number) =>
    setAccepted((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const handleApply = () => {
    if (!proposals) return;
    const chosen = proposals
      .filter((_, i) => accepted.has(i))
      .map(({ target, sources }) => ({ target, sources }));
    if (chosen.length === 0) {
      toast.info("Nothing selected to apply.");
      return;
    }
    onApply(chosen);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-30"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        onClick={() => !applying && onClose()}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="rounded-xl border max-w-2xl w-full pointer-events-auto flex flex-col"
          style={{
            backgroundColor: "#0F1419",
            borderColor: "rgba(229,231,235,0.08)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
            maxHeight: "85vh",
          }}
        >
          <header
            className="px-5 py-4 border-b flex items-start gap-3"
            style={{ borderColor: "rgba(229,231,235,0.08)" }}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
            >
              <Sparkles size={16} style={{ color: "#F97316" }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-white">Reorganize sections</h3>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                AI proposes how to merge over-fragmented sections back into their natural parents.
                Uncheck any you don't want; the rest will apply on click.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={applying}
              className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 flex-shrink-0 disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
                <Loader2 size={14} className="animate-spin" />
                Analyzing your sections…
              </div>
            ) : !proposals ? (
              <div className="text-sm text-gray-400 py-8 text-center">No data.</div>
            ) : proposals.length === 0 ? (
              <div className="text-sm py-8 text-center" style={{ color: "#9CA3AF" }}>
                <CheckCircle2 size={20} className="mx-auto mb-2" style={{ color: "#22c55e" }} />
                Sections look clean — nothing to merge.
              </div>
            ) : (
              <div className="space-y-3">
                {proposals.map((p, idx) => {
                  const isAccepted = accepted.has(idx);
                  const totalRows = p.sources.length;
                  return (
                    <div
                      key={idx}
                      className={
                        isAccepted
                          ? "rounded-lg border px-3 py-3"
                          : "rounded-lg border px-3 py-3 opacity-50"
                      }
                      style={{
                        backgroundColor: isAccepted
                          ? "rgba(249,115,22,0.04)"
                          : "rgba(255,255,255,0.02)",
                        borderColor: isAccepted ? "rgba(249,115,22,0.2)" : "rgba(229,231,235,0.06)",
                      }}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAccepted}
                          onChange={() => toggleAccepted(idx)}
                          disabled={applying}
                          className="mt-1 w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            Merge {totalRows} section
                            {totalRows === 1 ? "" : "s"} into{" "}
                            <span className="font-semibold text-orange-300">{p.target}</span>
                          </p>
                          <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
                            {p.reason}
                          </p>
                          <details className="mt-2">
                            <summary
                              className="text-[11px] cursor-pointer hover:text-white"
                              style={{ color: "#6B7280" }}
                            >
                              Show source sections
                            </summary>
                            <ul className="mt-2 ml-2 space-y-0.5">
                              {p.sources.map((s) => (
                                <li key={s} className="text-[11px]" style={{ color: "#9CA3AF" }}>
                                  · {s}
                                </li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {proposals && proposals.length > 0 && (
            <footer
              className="px-5 py-3 border-t flex items-center justify-between gap-3"
              style={{ borderColor: "rgba(229,231,235,0.08)" }}
            >
              <p className="text-xs" style={{ color: "#9CA3AF" }}>
                {accepted.size} of {proposals.length} merge
                {proposals.length === 1 ? "" : "s"} selected
              </p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onClose} disabled={applying}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<CheckCircle2 size={14} />}
                  onClick={handleApply}
                  loading={applying}
                  disabled={accepted.size === 0}
                >
                  Apply selected
                </Button>
              </div>
            </footer>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Filter chip + label
// ---------------------------------------------------------------------------

/**
 * One filter group laid out as a labelled row: a fixed-width label on the
 * left, chips wrapping on the right. Stacking groups vertically (instead of
 * cramming them onto one wrapping line) gives each its own visual track.
 */
function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-xs">
      <span
        className="w-16 pt-1.5 text-[11px] uppercase tracking-wider font-bold flex-shrink-0"
        style={{ color: "#E5E7EB" }}
      >
        {label}
      </span>
      <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">{children}</div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "px-2.5 py-1 rounded-full text-xs font-medium border bg-orange-500/15 text-orange-300 border-orange-500/30 capitalize"
          : "px-2.5 py-1 rounded-full text-xs font-medium border bg-white/[0.03] text-gray-400 border-white/10 hover:bg-white/[0.06] hover:text-white capitalize"
      }
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section group (collapsible) — with select-all
// ---------------------------------------------------------------------------

interface SectionGroupProps {
  section: string;
  items: ResponseMatrixItem[];
  total: number;
  done: number;
  /** Open ack-style rows (compliance/admin) in this section. */
  ackOpen: number;
  /** Open deliverable-style rows in this section. */
  deliverableOpen: number;
  /** Open pricing rows in this section. */
  pricingOpen: number;
  /** Total open rows in this section (sum of the above + any other types). */
  totalOpen: number;
  collapsed: boolean;
  onToggle: () => void;
  onSelectAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  orgUsers: OrgUserSummary[];
  usersById: Map<string, OrgUserSummary>;
  myId: string | null;
  onPatch: (itemId: string, patch: Partial<ResponseMatrixItem>) => void;
  onDelete: (item: ResponseMatrixItem) => void;
  onOpen: (itemId: string) => void;
  /** Mark every open compliance/admin row in this section as complete. */
  onAcknowledgeAll: () => void;
  /** A bulk operation is currently in flight — disables the ack-all button. */
  bulkBusy: boolean;
}

function SectionGroup({
  section,
  items,
  total,
  done,
  ackOpen,
  deliverableOpen,
  pricingOpen,
  totalOpen,
  collapsed,
  onToggle,
  onSelectAll,
  allSelected,
  someSelected,
  selectedIds,
  onToggleSelect,
  orgUsers,
  usersById,
  myId,
  onPatch,
  onDelete,
  onOpen,
  onAcknowledgeAll,
  bulkBusy,
}: SectionGroupProps) {
  const allDone = total > 0 && done === total;
  const progressPct = total === 0 ? 0 : Math.round((done / total) * 100);

  // Pick a dominant work-mode hint. 80% threshold so a few off-type rows
  // don't change the section's character.
  const dominance =
    totalOpen === 0
      ? "done"
      : ackOpen / totalOpen >= 0.8
        ? "ack"
        : deliverableOpen / totalOpen >= 0.8
          ? "deliverable"
          : pricingOpen / totalOpen >= 0.8
            ? "pricing"
            : "mixed";

  const hintText =
    dominance === "ack"
      ? `${ackOpen} to acknowledge`
      : dominance === "deliverable"
        ? `${deliverableOpen} to draft`
        : dominance === "pricing"
          ? `${pricingOpen} to price`
          : dominance === "done"
            ? "all done"
            : `${totalOpen} open`;

  const hintColor =
    dominance === "ack"
      ? "rgba(249,115,22,0.8)" // orange — attention without alarm
      : dominance === "deliverable"
        ? "rgba(99,102,241,0.85)" // indigo — heavy work
        : dominance === "pricing"
          ? "rgba(34,197,94,0.85)" // green — financial
          : dominance === "done"
            ? "#22c55e"
            : "#9CA3AF";

  const showAcknowledgeAll = dominance === "ack" && ackOpen > 0 && !collapsed;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: "#161B22",
        borderColor: "rgba(229,231,235,0.08)",
      }}
    >
      {/* Group header */}
      <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={onSelectAll}
          onClick={(e) => e.stopPropagation()}
          className="w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer"
          title={`Select all ${items.length} in section`}
        />
        <button onClick={onToggle} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          {collapsed ? (
            <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
          )}
          <span className="text-sm font-semibold text-white truncate flex-1">{section}</span>
          {/* Dominant work-mode hint — tells the user what kind of work this section is at a glance */}
          {totalOpen > 0 && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold flex-shrink-0 px-2 py-0.5 rounded-full"
              style={{
                color: hintColor,
                backgroundColor: "rgba(255,255,255,0.04)",
              }}
              title={
                dominance === "ack"
                  ? "Mostly compliance/admin clauses — read and acknowledge"
                  : dominance === "deliverable"
                    ? "Mostly deliverables — needs writing"
                    : dominance === "pricing"
                      ? "Pricing items — needs cost work"
                      : "Mixed work types"
              }
            >
              {hintText}
            </span>
          )}
          <span
            className="text-xs flex-shrink-0"
            style={{ color: allDone ? "#22c55e" : "#9CA3AF" }}
          >
            {done} of {total} done
          </span>
          <div
            className="w-16 h-1 rounded-full overflow-hidden flex-shrink-0"
            style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${progressPct}%`,
                backgroundColor: allDone ? "#22c55e" : "#F97316",
              }}
            />
          </div>
        </button>

        {/* Acknowledge-all — only on ack-dominant sections, only when expanded */}
        {showAcknowledgeAll && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAcknowledgeAll();
            }}
            disabled={bulkBusy}
            className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-orange-500/30 hover:bg-orange-500/10 flex-shrink-0 disabled:opacity-50"
            title={`Mark all ${ackOpen} pending compliance/admin items as acknowledged`}
          >
            <CheckCircle2 size={12} />
            Acknowledge all
          </button>
        )}
      </div>

      {/* Rows */}
      {!collapsed && (
        <div className="divide-y" style={{ borderColor: "rgba(229,231,235,0.05)" }}>
          {items.map((item) => (
            <MatrixRow
              key={item.id}
              item={item}
              owner={item.ownerUserId ? (usersById.get(item.ownerUserId) ?? null) : null}
              orgUsers={orgUsers}
              myId={myId}
              selected={selectedIds.has(item.id)}
              onToggleSelect={() => onToggleSelect(item.id)}
              onPatch={(patch) => onPatch(item.id, patch)}
              onDelete={() => onDelete(item)}
              onOpen={() => onOpen(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matrix row — with checkbox, owner picker, type-aware status, kebab menu
// ---------------------------------------------------------------------------

interface MatrixRowProps {
  item: ResponseMatrixItem;
  owner: OrgUserSummary | null;
  orgUsers: OrgUserSummary[];
  myId: string | null;
  selected: boolean;
  onToggleSelect: () => void;
  onPatch: (patch: Partial<ResponseMatrixItem>) => void;
  onDelete: () => void;
  onOpen: () => void;
}

function MatrixRow({
  item,
  owner,
  orgUsers,
  myId,
  selected,
  onToggleSelect,
  onPatch,
  onDelete,
  onOpen,
}: MatrixRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const statusOptions = statusOptionsForType(item.type);
  const isNa = item.notApplicable;
  const isDone = !isNa && item.status === "complete";
  // A row that's been synced to a PM tool. Status + owner are read-only in
  // Callaloo for those rows — the external tool is the source of truth.
  // (Legacy `teamworkTaskId` falls back here so existing data still locks.)
  const isSynced = Boolean(item.externalTaskId ?? item.teamworkTaskId);

  /**
   * Compliance and admin rows render in a compact single-line mode: a
   * checkbox, the requirement text, the type pill, and an "Acknowledge"
   * action — no owner picker, no status select, no priority badge. These
   * rows are usually batch-cleared by whoever does the compliance pass;
   * the full management chrome wastes space.
   *
   * Synced rows skip compact mode so the read-only state is unmistakable.
   */
  const isAckType = item.type === "compliance" || item.type === "admin";
  const useCompactRender = isAckType && !isSynced;

  if (useCompactRender) {
    return (
      <div
        className={
          isNa
            ? "px-4 py-1.5 flex items-center gap-3 opacity-50"
            : "px-4 py-1.5 flex items-center gap-3 hover:bg-white/[0.015] transition-colors"
        }
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer flex-shrink-0"
        />

        <button
          onClick={onOpen}
          className={
            isDone
              ? "text-xs text-gray-400 line-through truncate flex-1 min-w-0 text-left hover:text-white"
              : "text-xs text-white truncate flex-1 min-w-0 text-left hover:text-orange-300"
          }
          title="Open details"
        >
          {item.clauseRef && (
            <span className="font-mono mr-1.5 text-[10px]" style={{ color: "#6B7280" }}>
              {item.clauseRef}
            </span>
          )}
          {item.requirement}
        </button>

        <TypeBadge type={item.type} />

        {/* Acknowledge button (or Acknowledged pill) */}
        {isNa ? (
          <span className="text-xs text-gray-500 px-2.5 py-1 rounded-md border border-gray-500/20 bg-gray-500/10 flex-shrink-0">
            N/A
          </span>
        ) : isDone ? (
          <button
            onClick={() => onPatch({ status: "not_started" })}
            className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-md hover:bg-white/5 flex-shrink-0"
            style={{ color: "#22c55e" }}
            title="Click to undo"
          >
            <CheckCircle2 size={12} /> Acknowledged
          </button>
        ) : (
          <button
            onClick={() => onPatch({ status: "complete" })}
            className="text-xs text-gray-200 hover:text-white inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-white/10 hover:bg-white/5 flex-shrink-0"
            title={
              item.type === "compliance"
                ? "Mark this clause acknowledged"
                : "Mark this admin item done"
            }
          >
            Acknowledge
          </button>
        )}

        {/* Kebab menu — same shape as the full row */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
            title="More actions"
          >
            <MoreVertical size={14} />
          </button>

          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-48 rounded-lg border shadow-xl py-1 z-10"
              style={{
                backgroundColor: "#1F2933",
                borderColor: "rgba(229,231,235,0.12)",
              }}
            >
              <button
                onClick={() => {
                  onPatch({ notApplicable: !item.notApplicable });
                  setMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/5 flex items-center gap-2"
              >
                <Ban size={12} />
                {item.notApplicable ? "Mark applicable" : "Mark not applicable"}
              </button>
              <div className="my-1 border-t" style={{ borderColor: "rgba(229,231,235,0.06)" }} />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
                className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2"
              >
                <Trash2 size={12} />
                Delete row
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full chrome render path (deliverables / pricing / synced rows) ---------
  return (
    <div
      className={
        isNa
          ? "px-4 py-3 flex items-center gap-3 opacity-50"
          : "px-4 py-3 flex items-center gap-3 hover:bg-white/[0.015] transition-colors"
      }
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        className="w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer flex-shrink-0"
      />

      <div className="grid grid-cols-12 gap-3 items-center flex-1 min-w-0">
        <div className="col-span-3 min-w-0">
          <button
            onClick={onOpen}
            className={
              isDone
                ? "text-xs text-gray-400 line-through truncate w-full text-left hover:text-white"
                : "text-xs text-white truncate w-full text-left hover:text-orange-300"
            }
            title="Open details"
          >
            {item.clauseRef && (
              <span className="font-mono mr-1.5 text-[10px]" style={{ color: "#6B7280" }}>
                {item.clauseRef}
              </span>
            )}
            {item.requirement}
          </button>
        </div>

        {/* Owner cell */}
        <div className="col-span-2 min-w-0">
          {isSynced ? (
            // Synced: owner is managed in the external PM tool. Render
            // read-only with a tooltip nudging the user to change it there.
            <div
              className="flex items-center gap-1.5 text-xs px-1.5 py-1 max-w-full"
              title="Managed in your project-management tool — open the task to reassign."
            >
              <Avatar user={owner} size={20} />
              <span className="truncate" style={{ color: owner ? "#9CA3AF" : "#6B7280" }}>
                {owner ? userLabel(owner) : "—"}
              </span>
            </div>
          ) : (
            <div ref={pickerRef} className="relative">
              <button
                onClick={() => setPickerOpen((v) => !v)}
                className="flex items-center gap-1.5 text-xs hover:bg-white/5 rounded-lg px-1.5 py-1 max-w-full"
                title={owner ? userLabel(owner) : "Assign someone"}
              >
                <Avatar user={owner} size={20} />
                <span className="truncate" style={{ color: owner ? "#fff" : "#6B7280" }}>
                  {owner ? userLabel(owner) : "Assign"}
                </span>
              </button>
              {pickerOpen && (
                <UserPickerMenu
                  users={orgUsers}
                  myId={myId}
                  allowUnassign={Boolean(owner)}
                  onPick={(id) => {
                    setPickerOpen(false);
                    onPatch({ ownerUserId: id });
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="col-span-2">
          <TypeBadge type={item.type} />
        </div>

        <div className="col-span-1">
          <PriorityBadge priority={item.priority} />
        </div>

        <div className="col-span-3">
          {isNa ? (
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusPillClass(item)}`}
            >
              N/A
            </span>
          ) : isSynced ? (
            // Synced row: status is owned by the external tool. Render as a
            // read-only pill with the type-aware label. Click → open side
            // panel, where the "Open in [tool]" link lets you change it.
            <button
              onClick={onOpen}
              className={`inline-flex items-center w-full justify-start px-2.5 py-1.5 rounded-lg text-xs font-medium border ${statusPillClass(item)}`}
              title="Status is managed in your project-management tool. Open the side panel for the task link."
            >
              {statusOptions.find((o) => o.value === item.status)?.label ?? item.status}
            </button>
          ) : (
            <select
              value={item.status}
              onChange={(e) => onPatch({ status: e.target.value as MatrixItemStatus })}
              className={`w-full bg-transparent text-xs border rounded-lg px-2 py-1.5 outline-none ${statusPillClass(item)}`}
            >
              {statusOptions.map((o) => (
                <option
                  key={o.value}
                  value={o.value}
                  style={{ backgroundColor: "#111318", color: "#fff" }}
                >
                  {o.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="col-span-1 text-right flex items-center justify-end gap-1.5">
          {isSynced && (
            <span title="Synced to your project-management tool">
              <CheckCircle2 size={14} style={{ color: "#22c55e" }} />
            </span>
          )}

          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
              title="More actions"
            >
              <MoreVertical size={14} />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-48 rounded-lg border shadow-xl py-1 z-10"
                style={{
                  backgroundColor: "#1F2933",
                  borderColor: "rgba(229,231,235,0.12)",
                }}
              >
                <button
                  onClick={() => {
                    onPatch({ notApplicable: !item.notApplicable });
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/5 flex items-center gap-2"
                >
                  <Ban size={12} />
                  {item.notApplicable ? "Mark applicable" : "Mark not applicable"}
                </button>
                <div className="my-1 border-t" style={{ borderColor: "rgba(229,231,235,0.06)" }} />
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                >
                  <Trash2 size={12} />
                  Delete row
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Teamwork sync button — pushes every applicable, unsynced item.
// ---------------------------------------------------------------------------

function TeamworkSyncButton({
  projectId,
  items,
}: {
  projectId: string;
  items: ResponseMatrixItem[];
}) {
  const qc = useQueryClient();
  const syncableCount = items.filter((i) => !i.notApplicable && !i.teamworkTaskId).length;

  const sync = useMutation({
    mutationFn: () => api.syncToTeamwork(projectId),
    onSuccess({ createdCount, errors }) {
      qc.invalidateQueries({ queryKey: matrixKey(projectId) });
      if (errors && errors.length > 0) {
        toast.warning(
          `Synced ${createdCount}; ${errors.length} item${errors.length === 1 ? "" : "s"} failed.`,
        );
      } else if (createdCount === 0) {
        toast.info("Nothing new to sync — every item already has a Teamwork task.");
      } else {
        toast.success(`Synced ${createdCount} item${createdCount === 1 ? "" : "s"} to Teamwork.`);
      }
    },
    onError(err) {
      toast.error((err as Error).message);
    },
  });

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => sync.mutate()}
      disabled={sync.isPending || items.length === 0}
      icon={sync.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
    >
      {sync.isPending
        ? "Syncing…"
        : syncableCount === 0
          ? "All synced"
          : `Sync ${syncableCount} to Teamwork`}
    </Button>
  );
}
